/**
 * IntakeLanding — the `/intake/:linkId` landing route (M5 Flag B, AD-022/AD-023).
 *
 * The RECEIVING half of the intake-link loop. Staff send
 * `https://portal.aayatanveda.in/intake/<linkId>` (S2 generateIntakeLink);
 * a recipient clicks it and lands HERE. Flow (decision table = lib/intakeLanding):
 *
 *   1. Session restoring        → "loading"
 *   2. Not signed in            → "signin": reuse the EXISTING Google/Apple
 *      popup auth (useAuth().signInGoogle / signInApple) — NO new auth, NO
 *      navigation away, so we return to this route in place once `user` lands.
 *      Clients without a Google account get a link to /login's OTP code path.
 *   3. Signed in                → "resolving": call the LIVE
 *      resolveClientClaimsOnSignIn({uid,email}) (R-AUTH option (a) — match by
 *      signed-in email, NO functions edit), force getIdToken(true) to refresh
 *      claims, then read intakeLinks/{linkId} (now readable under isClient()),
 *      validate active + not-expired (lib/intakeLanding.linkVerdict).
 *   4. Claims minted + link OK  → "route": navigate to the existing StageEngine
 *      form (routes/Intake.tsx) at /intake?tier=&client=&stage=consent.
 *   5. Any failure              → "softfail": friendly, dead-end-free notice
 *      (owner's soften-hard-fails rule), with a contact path.
 *
 * Tokens only — no hex / Tailwind colours (LOCKED ONE UI system).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { appleSignInAvailable } from '../lib/platform';
import {
  landingPhase,
  linkVerdict,
  intakeTargetPath,
  softFailCopy,
  routableContactId,
  type IntakeLinkDoc,
  type LinkVerdict,
  type LandingPhase,
} from '../lib/intakeLanding';

/** AV public contact (memory: reference_av_phone_numbers — public number). */
const SUPPORT_EMAIL = 'support@aayatanveda.com';

export default function IntakeLanding() {
  const { linkId = '' } = useParams<{ linkId: string }>();
  const { user, loading, signInGoogle, signInApple } = useAuth();
  const showApple = appleSignInAvailable();

  // Resolution state (claims mint + link read). Driven once per signed-in user.
  const [resolving, setResolving] = useState(false);
  const [claimsMinted, setClaimsMinted] = useState(false);
  const [verdict, setVerdict] = useState<LinkVerdict | null>(null);
  const [softReason, setSoftReason] = useState<LinkVerdict | 'email_mismatch' | 'error' | null>(null);
  const [linkDoc, setLinkDoc] = useState<IntakeLinkDoc | null>(null);
  /** Guards the resolve effect against React-18 strict double-run + churn. */
  const startedRef = useRef(false);

  const resolve = useCallback(async () => {
    if (!user || startedRef.current) return;
    startedRef.current = true;
    setResolving(true);
    try {
      const email = user.email || '';
      if (!email) {
        // No verified email on the account → cannot match a link by email.
        setSoftReason('email_mismatch');
        setResolving(false);
        return;
      }

      // ── Mint the canonical client claim via the LIVE callable (R-AUTH (a)) ──
      const [{ functions, db, auth }, { httpsCallable }, { doc, getDoc }] =
        await Promise.all([
          import('../lib/firebase'),
          import('firebase/functions'),
          import('firebase/firestore'),
        ]);
      const fn = httpsCallable<
        { uid: string; email: string },
        { success: boolean; reason?: string; contactId?: string }
      >(functions, 'resolveClientClaimsOnSignIn');
      const res = await fn({ uid: user.uid, email });

      if (!res.data?.success) {
        // No active link matched THIS signed-in email — the client likely used
        // a different Google account than the one on file. Soft-fail (no dead-end).
        setSoftReason('email_mismatch');
        setResolving(false);
        return;
      }

      // Force-refresh so the freshly-minted isClient/contactId claims take effect
      // NOW (auth.tsx also re-resolves on the next onAuthStateChanged, but the
      // link read below needs the claim immediately).
      await auth.currentUser?.getIdToken(true).catch(() => {
        /* non-fatal — claim already set server-side; read may still pass */
      });
      setClaimsMinted(true);

      // ── Read THIS link doc (now permitted under isClient() + matching cid) ──
      const snap = await getDoc(doc(db, 'intakeLinks', linkId));
      const data = (snap.exists() ? (snap.data() as IntakeLinkDoc) : null);
      const v = linkVerdict(data, snap.exists(), Date.now());

      // Defense-in-depth: the minted contactId must match this link's contactId
      // (same client). On a mismatch, treat as email_mismatch rather than route
      // a client into another client's form.
      if (v === 'ok' && data && routableContactId(data) !== res.data.contactId) {
        setSoftReason('email_mismatch');
        setResolving(false);
        return;
      }

      setLinkDoc(data);
      setVerdict(v);
      if (v !== 'ok') setSoftReason(v);
      setResolving(false);
    } catch {
      setSoftReason('error');
      setResolving(false);
    }
  }, [user, linkId]);

  useEffect(() => {
    if (user && !loading) void resolve();
  }, [user, loading, resolve]);

  const phase: LandingPhase = landingPhase({
    authLoading: loading,
    hasUser: !!user,
    resolving,
    softFail: softReason != null,
    claimsMinted,
    verdict,
  });

  // ── route: hand off to the existing StageEngine intake form ──
  if (phase === 'route' && linkDoc) {
    return <Navigate to={intakeTargetPath(linkDoc)} replace />;
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="mark" aria-label="Aayatan Veda">
          <img src="/av-mark.svg" alt="Aayatan Veda" />
        </div>

        {phase === 'loading' && (
          <p data-testid="landing-loading" style={msg}>Opening your intake form…</p>
        )}

        {phase === 'resolving' && (
          <p data-testid="landing-resolving" style={msg}>
            Just a moment — preparing your intake form…
          </p>
        )}

        {phase === 'signin' && (
          <div data-testid="landing-signin">
            <h1 className="serif">Your intake form</h1>
            <p>
              Sign in to open your Aayatan Veda intake form. Use the{' '}
              <strong>same email address you gave Aayatan Veda</strong> so we can
              find your form.
            </p>
            <div className="auth-btns">
              <button className="btn3 gh" onClick={signInGoogle}>
                <span aria-hidden>G</span> Continue with Google
              </button>
              {showApple && (
                <button className="btn3 gh" onClick={signInApple}>
                  <span aria-hidden></span> Continue with Apple
                </button>
              )}
            </div>
            <a className="auth-codelink" href="/login">
              No Google account? Use a code instead
            </a>
          </div>
        )}

        {phase === 'softfail' && softReason && (
          <div data-testid="landing-softfail" role="status">
            <h1 className="serif">{softFailCopy(softReason).title}</h1>
            <p>{softFailCopy(softReason).body}</p>
            {softReason === 'email_mismatch' && (
              <div className="auth-btns" style={{ marginTop: 4 }}>
                <button className="btn3 gh" onClick={signInGoogle}>
                  <span aria-hidden>G</span> Try a different Google account
                </button>
              </div>
            )}
            <p style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-mute)' }}>
              Need help? Email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--accent)' }}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </div>
        )}

        <div className="auth-foot">portal.aayatanveda.in</div>
      </div>
    </div>
  );
}

const msg: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--ink-mute)',
  textAlign: 'center',
  padding: '8px 0',
};
